import type { HomeNetworkRedirectVariables } from '@ms/yammer-graphql';
import queryHomeNetworkRedirect from '@ms/yammer-graphql/dist/operations/queryHomeNetworkRedirect.graphql';
import { reportError } from '@ms/yammer-telemetry';

import type { GraphqlApiRequest } from '../..';
import { getGraphqlApiRequest } from '../..';
import apiAction from '../../api/apiAction';
import type { PromiseThunkAction } from '../../types';
import { getExperimentTreatmentIfActive } from '../experiments/selectors';

import type { HomeNetworkRedirectResponse } from './actions.types';

const loadHomeNetworkRedirectDataRequest = (
  resourceId: string | null
): GraphqlApiRequest<HomeNetworkRedirectVariables> =>
  getGraphqlApiRequest({
    body: {
      operationName: 'HomeNetworkRedirect',
      query: queryHomeNetworkRedirect,
      variables: {
        resourceId,
      },
    },
  });

type GetRedirectLink = (
  permalink: string | null,
  resourceId: string | null,
  redirectResourceId: string | null
) => string | undefined;
export const getRedirectLink: GetRedirectLink = (permalink, resourceId, redirectResourceId) => {
  const pathname = window.location.pathname;
  const splitPath = pathname.split('/');
  const orgIndex = splitPath.findIndex((path) => path === 'org');

  if (permalink === splitPath[orgIndex + 1] || orgIndex === -1 || permalink == null) {
    return undefined;
  }
  splitPath[orgIndex + 1] = permalink;

  const joinedUrl = `${window.location.origin}${splitPath.join('/')}`;

  if (resourceId == null || redirectResourceId == null) {
    return joinedUrl;
  }

  return joinedUrl.replace(resourceId, redirectResourceId);
};

type Load = (resourceId: string | null) => PromiseThunkAction<void>;
export const load: Load = (resourceId) => async (dispatch, getState) => {
  const state = getState();
  const urlRedirectEnabled = getExperimentTreatmentIfActive(state, 'MultiTenantOrganization');

  if (!urlRedirectEnabled || !isValidResourceId(resourceId)) {
    return;
  }

  const request = loadHomeNetworkRedirectDataRequest(resourceId);
  const action = apiAction(request);

  try {
    await dispatch(action).then((response: HomeNetworkRedirectResponse) => {
      if (response.data.viewer.homeNetworkRedirect?.shouldRedirectToHomeNetwork) {
        const redirectLink = getRedirectLink(
          response.data.viewer.homeNetworkRedirect?.networkPermalink,
          resourceId,
          response.data.viewer.homeNetworkRedirect?.redirectResourceId
        );
        if (redirectLink) {
          window.location.replace(redirectLink);
        }
      }
    });
  } catch (error) {
    reportError({ error });
  }
};

const isValidResourceId = (resourceId: string | null) => {
  if (resourceId == null) {
    return true;
  }

  // Not all urls have a valid id, determine if it is a valid base64 by decoding and encoding it again.
  // Base64 values in urls will not have padding, so removing padding on the encoded string for a valid comparison
  // eslint-disable-next-line no-restricted-globals
  const encoded = removePadding(btoa(atob(resourceId)));
  if (resourceId.length > 4 && encoded === resourceId) {
    return true;
  }

  return false;
};

const removePadding = (encoded: string) => {
  const regex = new RegExp('=' + '+$');

  return encoded.replace(regex, '');
};
